/**
 * CustomerCreatePage — `customers/new`: create a customer in CIS with its
 * account address, market and sales rep and CSR (v1 completion C5,
 * docs/features/v1-completion/C_customer_master_spec.md §2.9; mock-up
 * docs/features/v1-completion/mockups/C_create_customer.html, states 2-6).
 *
 * Behind `v1_completion` (the route's feature) and `customers:edit`. Every
 * field is checked by the server and the problems come back together, bound
 * under their inputs; nothing is created then. Created, it lands on the
 * customer's page with "Customer created" (Ben, d56).
 */
import { useEffect, useId, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { NewCustomerForm } from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ORDER_CONTACT_NOT_SAVED_NOTE,
  bindNewCustomerErrors,
  emptyNewCustomerDraft,
  fieldsNeedAFix,
  toCreateCisCustomerRequest,
  wontSeeNewCustomer,
  type NewCustomerDraft,
  type NewCustomerField,
} from "@shared-core/domain/new-customer";
import { paths } from "@shared-core/routing/routes";
import { useCanEditCustomers } from "../app/auth/hooks";
import {
  NewCustomerFields,
  NotInPortfolioNotice,
  type NewCustomerSection,
} from "../app/components/domain/customers/NewCustomerFields";
import { Button } from "../app/components/primitives/button";

const SECTION_HEADINGS: Record<NewCustomerSection, ReactNode> = {
  customer: "Customer",
  address: (
    <>
      Account address{" "}
      <span className="text-xs font-normal text-muted-foreground">
        (optional: add it later on the customer)
      </span>
    </>
  ),
  owners: "Sales rep and CSR",
  orderContact: (
    <>
      Order contact{" "}
      <span className="text-xs font-normal text-muted-foreground">
        (optional)
      </span>
    </>
  ),
};

type Failure =
  | { kind: "fields"; count: number; unbound: string[] }
  | { kind: "message"; message: string };

export function CustomerCreatePage() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const canEdit = useCanEditCustomers();
  const idPrefix = useId();
  const [form, setForm] = useState<NewCustomerForm | null>(null);
  const [formFailed, setFormFailed] = useState(false);
  const [draft, setDraft] = useState<NewCustomerDraft>(() =>
    emptyNewCustomerDraft(null),
  );
  const [errors, setErrors] = useState<
    Partial<Record<NewCustomerField, string>>
  >({});
  const [failure, setFailure] = useState<Failure | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adapter
      .getNewCustomerForm()
      .then((loaded) => {
        if (cancelled) return;
        setForm(loaded);
        // The defaults arrive with the form; keep anything already typed.
        setDraft((current) => ({
          ...current,
          salesRepUserId: loaded.default_sales_rep_user_id ?? "",
          csrUserId: loaded.default_csr_user_id ?? "",
        }));
      })
      .catch(() => {
        if (!cancelled) setFormFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const create = async () => {
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      const created = await adapter.createCisCustomer(
        toCreateCisCustomerRequest(draft, { withOrderContact: true }),
      );
      if (created.order_contact_not_saved)
        toast.warning(ORDER_CONTACT_NOT_SAVED_NOTE);
      else toast.success("Customer created");
      navigate(
        paths.customerDetail({
          customerId: created.tharstern_customer_code ?? created.customer_code,
        }),
      );
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        const bound = bindNewCustomerErrors(fieldErrors);
        setErrors(bound.byField);
        setFailure({
          kind: "fields",
          count: bound.count,
          unbound: bound.unbound,
        });
      } else {
        setFailure({
          kind: "message",
          message: mutationErrorMessage(
            err,
            "The customer wasn't created. Try again.",
          ),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="space-y-4">
        <BackLink />
        <h1 className="text-2xl">New customer</h1>
        <p className="text-sm text-muted-foreground">
          Creating customers is for sales reps, CSRs, marketing, managers and
          admins.
        </p>
      </div>
    );
  }

  const outOfSight = form !== null && wontSeeNewCustomer(form, draft);

  return (
    <form
      className="mx-auto max-w-4xl space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void create();
      }}
    >
      <BackLink />
      <h1 className="text-2xl">New customer</h1>

      {failure && (
        <div
          role="alert"
          className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:text-red-400"
        >
          {failure.kind === "message"
            ? failure.message
            : fieldsNeedAFix(failure.count)}
          {failure.kind === "fields" &&
            failure.unbound.map((message) => (
              <span key={message} className="mt-1 block">
                {message}
              </span>
            ))}
        </div>
      )}
      {outOfSight && <NotInPortfolioNotice />}
      {formFailed && (
        <p role="status" className="text-sm text-muted-foreground">
          The sales rep and CSR lists didn&apos;t load: the customer goes to
          House Account until someone sets them on its Settings tab.
        </p>
      )}

      <NewCustomerFields
        idPrefix={idPrefix}
        form={form}
        draft={draft}
        onDraftChange={setDraft}
        errors={errors}
        disabled={saving}
        showOrderContact
        sectionFrame={(section, content) => (
          <section
            aria-labelledby={`${idPrefix}-${section}-heading`}
            className="space-y-3 rounded-lg border border-border bg-card p-5"
          >
            <h2
              id={`${idPrefix}-${section}-heading`}
              className="text-base font-semibold"
            >
              {SECTION_HEADINGS[section]}
            </h2>
            {content}
          </section>
        )}
      />

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(paths.customers())}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="size-3.5 animate-spin" />}
          Create customer
        </Button>
      </div>
    </form>
  );
}

function BackLink() {
  return (
    <Link
      to={paths.customers()}
      className="text-sm text-primary underline-offset-2 hover:underline"
    >
      ← Customers
    </Link>
  );
}
